/**
 * Chronological campaign list. The last entry is treated as the live campaign (we don't have
 * empty gaps between draws). Add new campaigns at the bottom.
 */
export const campaigns = [
  { name: 'DECEMBER_2023', landing: '/julekalender-2023', terms: '/julekalender-2023-vilkar' },
  { name: 'JANUARY_2024', landing: '/januar-trekning-2024', terms: '/januar-trekning-2024-vilkar' },
  { name: 'FEBRUARY_2024', landing: '/februar-trekning', terms: '/februar-trekning-vilkar' },
  { name: 'MARCH_2024', landing: '/paaske-trekning-2024', terms: '/paaske-trekning-2024-vilkar' },
  { name: 'APRIL_2024', landing: '/april-trekning-2024', terms: '/april-trekning-2024-vilkar' },
  { name: 'MAY_2024', landing: '/mai-trekning-2024', terms: '/mai-trekning-2024-vilkar' },
  { name: 'SUMMER_JUNE_2024', landing: '/sommer-trekning', terms: '/sommer-trekning-vilkar' },
  {
    name: 'SUMMER_JULY_2024',
    landing: '/sommer-trekning-forlengelse',
    terms: '/sommer-trekning-forlengelse-vilkar',
  },
  {
    name: 'SUMMER_EXTRA_2024',
    landing: '/august-trekning-2024',
    terms: '/august-trekning-2024-vilkar',
  },
  { name: 'WAKE_UP_2024', landing: '/host-trekning', terms: '/host-trekning-vilkar' },
  {
    name: 'WAKE_UP_PART2_2024',
    landing: '/host-trekning-forlengelse',
    terms: '/host-trekning-forlengelse-vilkar',
  },
  { name: 'ADVENT_2024', landing: '/julekalender-2024', terms: '/julekalender-2024-vilkar' },
  { name: 'JANUARY_2025', landing: '/januar-trekning', terms: '/januar-trekning-vilkar' },
  { name: 'JANUARY_OPEN_2025', landing: '/gjeldfri-januar', terms: '/gjeldfri-januar-vilkar' },
  { name: 'FEBRUARY_2025', landing: '/gjeldfri-februar', terms: '/gjeldfri-februar-vilkar' },
  { name: 'MARCH_2025', landing: '/paaske-trekning-2025', terms: '/paaske-trekning-2025-vilkar' },
  { name: 'MAY_2025', landing: '/gjeldfri-mai', terms: '/gjeldfri-mai-vilkar' },
  { name: 'MAY_MEMBERS_2025', landing: '/mai-trekning', terms: '/mai-trekning-vilkar' },
  { name: 'JUNE_2025', landing: '/juni-trekning-2025', terms: '/juni-trekning-2025-vilkar' },
  { name: 'JULY_2025', landing: '/juli-trekning-2025', terms: '/juli-trekning-2025-vilkar' },
  { name: 'AUGUST_2025', landing: '/august-trekning', terms: '/august-trekning-vilkar' },
  { name: 'SEPTEMBER_2025', landing: '/september-trekning', terms: '/september-trekning-vilkar' },
  { name: 'OCTOBER_2025', landing: '/oktober-trekning', terms: '/oktober-trekning-vilkar' },
  { name: 'ADVENT_2025', landing: '/julekalender', terms: '/julekalender-vilkar' },
  { name: 'JANUARY_2026', landing: '/nyttarstrekning', terms: '/nyttarstrekning-vilkar' },
  { name: 'MARCH_2026', landing: '/paaske-trekning', terms: '/paaske-trekning-vilkar' },
  {
    name: 'APRIL_2026',
    landing: '/april-trekning',
    terms: '/april-trekning-vilkar',
    article: '/april-trekning-artikkel',
  },
  { name: 'JUNE_2026', landing: '/juni-trekning', terms: '/juni-trekning-vilkar' },
  { name: 'JULY_2026', landing: '/juli-trekning', terms: '/juli-trekning-vilkar' },
  { name: 'SEPTEMBER_2026', landing: '/krymp-gjelden', terms: '/krymp-gjelden-vilkar' },
] as const satisfies readonly {
  name: string;
  landing: `/${string}`;
  terms: `/${string}`;
  article?: `/${string}`;
}[];

export type Campaign = (typeof campaigns)[number];

export const latestCampaign = campaigns[campaigns.length - 1];

export function getCampaignPaths(campaign: Campaign) {
  return [
    campaign.landing,
    campaign.terms,
    ...('article' in campaign && campaign.article ? [campaign.article] : []),
  ];
}

/** Landing plus campaign article; terms stay off the sitemap. */
export function getCampaignSitemapPaths(campaign: Campaign) {
  return [
    campaign.landing,
    ...('article' in campaign && campaign.article ? [campaign.article] : []),
  ];
}

type CampaignRoutes<T extends readonly Campaign[]> = {
  [C in T[number] as `${C['name']}_COMPETITION`]: C['landing'];
} & {
  [C in T[number] as `${C['name']}_COMPETITION_TERMS`]: C['terms'];
} & {
  [
    C in T[number] as C extends { article: string } ? `${C['name']}_COMPETITION_ARTICLE` : never
  ]: C extends { article: infer A extends string } ? A : never;
};

function toCampaignRoutes<const T extends readonly Campaign[]>(list: T): CampaignRoutes<T> {
  return Object.fromEntries(
    list.flatMap((campaign) => [
      [`${campaign.name}_COMPETITION`, campaign.landing],
      [`${campaign.name}_COMPETITION_TERMS`, campaign.terms],
      ...('article' in campaign && campaign.article
        ? [[`${campaign.name}_COMPETITION_ARTICLE`, campaign.article] as const]
        : []),
    ]),
  ) as CampaignRoutes<T>;
}

export const campaignRoutes = toCampaignRoutes(campaigns);

export const mainRoutes = {
  HOME: '/',
  PRODUCTS_REFINANCING: '/refinansiering',
  CREDIT_CARDS: '/kredittkort',
  CONSUMER_LOAN: '/forbrukslan',
  MY_ECONOMY: '/din-okonomi',
  ARTICLES: '/artikler',
  PRODUCTS_REFINANCING_CHAT: '/refinansiering/:flow/:step', // separate route to avoid conflict with PRODUCTS_FAQ_ARTICLE route
  LIVE_OFFERS: '/tilbud/:web_rec_id',
  LIVE_OFFERS_SURVEY: '/live-offers-survey/:web_rec_id',
  PRODUCTS_FAQ_ARTICLE: '/refinansiering/:articleName',
  CUSTOMER_SERVICE: '/kundeservice',
  TERMS_OF_USE_MIN_SCOOPR: '/brukervilkar-min-scoopr',
  TERMS_OF_USE_REFINANCING: '/brukervilkar-scoopr-refinansiering',
  TERMS_OF_USE_MORTGAGE: '/brukervilkar-scoopr-boliglan',
  PRIVACY_STATEMENT: '/personvernerklaering',
  COOKIES: '/cookies',
  ERROR_PAGE: '/oops',
  ABANDONED_SESSIONS_CHAT: '/soknad/:flow/:step',
  VERIFIED_REFINANCING: '/debt-check/:flow/:step',
  COMPETITION_WINNERS: '/tidligere-trekninger',
  LOGGED_OUT: '/logget-ut',
  LIVE_OFFERS_PREVIEW: '/preview/:web_rec_id',
  ...campaignRoutes,
} as const;

export const economyPagesDisabled = {
  LOAN_MONEY: '/lane-penger',
  UNSECURED_LOAN: '/laan-uten-sikkerhet',
  BEST_CONSUMER_LOAN: '/beste-forbrukslan',
  LOAN_CALCULATOR: '/forbrukslaan-kalkulator',
  CONSUMER_LOAN_WITH_PAYMENT_REMARK: '/forbrukslan-med-betalingsanmerkning',
  CHEAPEST_LOAN: '/billigste-forbrukslaan',
  ABOUT_LOAN: '/laan',
  PRIVATE_LOAN: '/privatlan',
};

export const myScooprPages = {
  MY_SCOOPR: '/min-scoopr',
  MY_SCOOPR_CONSENTS: '/min-scoopr/consents',
  MY_SCOOPR_LOGIN: '/min-scoopr/login',
  MY_SCOOPR_DASHBOARD: '/min-scoopr/dashboard',
  MY_SCOOPR_MY_CONSENTS: '/min-scoopr/personvern',
  MY_SCOOPR_MY_DEBT: '/min-scoopr/min-gjeld',
  MY_SCOOPR_EMAIL: '/min-scoopr/e-post',
  MY_SCOOPR_PHONE: '/min-scoopr/mobilnummer',
  MY_SCOOPR_SAVINGS_POTENTIAL: '/min-scoopr/mitt-sparepotensiale',
  MY_SCOOPR_LOGIN_LOADING: '/min-scoopr/loading',
  MY_SCOOPR_MY_CREDIT_SCORE: '/min-scoopr/min-kredittscore',
} as const;

export const economyPages = {
  MY_DEBT: '/mingjeld',
  CREDIT_CHECK: '/kredittscore',
  CREDIT_CHECK_MYSELF: '/kredittsjekke-seg-selv',
  PARTNER_BANKS: '/banker',
  PARTNER_BANK_DETAILS: '/banker/:bankKey',
  DEBT_REGISTER: '/gjeldsregisteret',
  SAVINGS_POTENTIAL: '/sparepotensialet',
  DEBT_INFORMATION: '/norsk-gjeldsinformasjon',
  INTEREST_RATE: '/rente-hva-er-egentlig-det',
  SMALL_LOAN: '/smalan',
  DEBT_FREE: '/gjeldfri',
  CREDIT_CARD_LOAN: '/kredittkortlan',
  DEBT_OVERVIEW: '/gjeldsoversikt',
  REPAYMENT_PERIOD: '/nedbetalingstid',
  COMPARE_LOANS: '/sammenlign-lan',
  REFINANCING_LOANS: '/refinansieringslan',
  COMPARE_CONSUMER_LOANS: '/sammenlign-forbrukslan',
  SAVE_OR_REPAY_LOAN: '/lonner-det-seg-a-spare-eller-nedbetale-lan',
  MICROLOAN: '/slik-fungerer-mikrolan',
  NOMINAL_AND_EFFECTIVE_INTEREST_RATE: '/nominell-og-effektiv-rente-hva-er-forskjellen',
  PROPERTY_VALUE: '/boligverdi',
} as const;

export const routes = {
  ...mainRoutes,
  ...economyPages,
  ...myScooprPages,
} as const;

export default routes;
